import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { LatLngTuple } from "leaflet";

import { CircleMarker, Tooltip } from "react-leaflet";
import ParkkiTheme from "themes/ParkkiTheme";

const StyledTooltip = styled(Tooltip)`
  &.leaflet-tooltip.leaflet-tooltip-center {
    font-size: 14px;
    padding: 2px 10px;
    background: ${({
      background: { fillValue, fillColor, defaultColor },
    }: {
      background: {
        fillValue: number;
        fillColor: string;
        defaultColor: string;
      };
    }) => {
      if (fillValue === null) {
        return "white";
      }

      return `linear-gradient(
      90deg,
      ${fillColor} ${fillValue}%,
      ${defaultColor} ${fillValue}%,
      ${defaultColor} 100%
    )`;
    }};
    border: 1px solid black;
    border-radius: 8px;
    color: black;
  }
`;

type Props = {
  label?: string | JSX.Element | null;
  offset: any;
  position: LatLngTuple;
  background?: {
    value?: number;
    total?: number;
    out?: number;
    in?: number;
    config?: {
      mainColor: string;
      unselectedColor: string;
      minColor: string;
      valueColors: {};
      unselectedValueColor: string;
    };
  };
  selected: boolean;
  type?: string;
  disabled?: boolean;
  onClick: Function;
  interactive?: boolean;
};

function MapTooltipLabel({
  label,
  offset,
  position,
  background,
  selected,
  disabled,
  onClick,
  interactive = true,
}: Props): JSX.Element | null {
  const fillPercentage = useMemo(() => {
    if (background?.out === 0) {
      return 100;
    }
    if (background?.in === 0) {
      return 0;
    }

    return (
      Math.round(((background?.value || 0) / (background?.total || 1)) * 100) ||
      0
    );
  }, [background]);

  const fillValuesFromConfig = useMemo(() => {
    return Object.keys(background?.config?.valueColors || {})?.sort() || [];
  }, [background]);

  const fillColor = useMemo(() => {
    if (!selected) return background?.config?.unselectedValueColor;
    const fillValue = fillValuesFromConfig.reduce(
      (prev: string, curr: string): string => {
        return parseInt(curr, 10) - (fillPercentage || 0) <= 0 ? curr : prev;
      },
      "0"
    );

    return (
      // @ts-ignore
      background?.config?.valueColors?.[fillValue]?.backgroundColor ||
      ParkkiTheme.colors.white
    );
  }, [background, fillPercentage, fillValuesFromConfig, selected]);

  const computedBackground = useMemo(() => {
    const defaultColor = selected
      ? background?.config?.mainColor
      : background?.config?.unselectedColor;

    return {
      fillValue: fillPercentage,
      defaultColor,
      fillColor,
    };
  }, [background, fillPercentage, fillColor, selected]);

  function handleOnClick() {
    if (disabled) return null;

    return onClick ? onClick() : null;
  }

  if (!label) {
    return null;
  }

  return (
    <CircleMarker
      interactive={true}
      center={position}
      radius={0}
      fillOpacity={0}
      opacity={0}
      onclick={handleOnClick}
    >
      <StyledTooltip
        offset={offset}
        // @ts-ignore
        background={computedBackground}
        direction="center"
        opacity={1}
        permanent
        interactive={interactive}
      >
        {label}
      </StyledTooltip>
    </CircleMarker>
  );
}

MapTooltipLabel.defaultProps = {
  selected: false,
  position: [0, 0],
  offset: [0, 0],
};

export default MapTooltipLabel;
